//#region Imágenes del contenido

export interface DocumentationMediaReference {
    linkCode: string;
    mediaId: string;
}

/**
 * Las imágenes del contenido apuntan a la ruta propia de la API
 * (`/api/v1/documentation/{linkCode}/media/{mediaId}`), absoluta o relativa según
 * cómo esté configurada allá. Ninguna de las dos formas sirve tal cual: el front
 * habla con el gateway, y además ese endpoint exige el token.
 *
 * El código de enlace se lee de la propia dirección en vez de darlo por sentado:
 * una página puede traer imágenes de otra.
 */
const MEDIA_PATH = /\/api\/v\d+\/documentation\/([^/]+)\/media\/([^/?#]+)/i;

export const readMediaReference = (src: string | null | undefined): DocumentationMediaReference | null => {
    if (!src) return null;

    const match = MEDIA_PATH.exec(src);
    if (!match) return null;

    try {
        return { linkCode: decodeURIComponent(match[1]), mediaId: decodeURIComponent(match[2]) };
    } catch {
        // Un `%` suelto en la dirección rompe el decode. La referencia sirve igual
        // sin decodificar: descartarla dejaría la imagen sin cargar por un detalle
        // de escapado.
        return { linkCode: match[1], mediaId: match[2] };
    }
};

//#endregion Imágenes del contenido

//#region Presentación

/** Fecha de última actualización, para que se vea si la ayuda quedó vieja. */
export const formatLastModified = (value?: Date | string | null): string | undefined => {
    if (!value) return undefined;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;

    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Una página sin cuerpo existe pero no tiene nada que mostrar. Pasa cuando el
 * enlace apunta a algo que se vació del otro lado, y se avisa como tal en vez de
 * abrir un panel en blanco.
 */
export const hasRenderableContent = (content?: string | null): boolean =>
    typeof content === 'string' && content.trim().length > 0;

//#endregion Presentación
